import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, toDisplayString as _toDisplayString, Fragment as _Fragment, renderList as _renderList } from "vue"

const _hoisted_1 = { class: "col-lg-9 mx-auto" }
const _hoisted_2 = { class: "text-center mb-4" }
const _hoisted_3 = { class: "container-fluid px-0 mt-0" }
const _hoisted_4 = {
  key: 0,
  class: "alert alert-warning rounded pt-4",
  role: "alert"
}
const _hoisted_5 = { class: "card bg-light" }
const _hoisted_6 = { class: "card-body" }
const _hoisted_7 = { class: "form-check form-switch me-2" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = {
  class: "form-check-label text-muted todays-money-label ms-3",
  for: "consentToProductNotificationsCheckboxId"
}
const _hoisted_10 = ["innerHTML"]
const _hoisted_11 = { class: "container-fluid" }

import { ref, computed, watch } from "vue";
import WorkflowNavigation from "@/guiide/components/common/WorkflowNavigation.vue";
import SubSection from "./common/SubSection.vue";
import InvestmentAllocation from "./secure/InvestmentAllocation.vue";
import QuestionAndAnswer from "./common/QuestionAndAnswer.vue";
import Apply from "./secure/Apply.vue";
import PageNav from "@/guiide/components/common/PageNav.vue";
import DynamicAdviceViewer from "./common/DynamicAdviceViewer.vue";
import { useStore } from "vuex";
import { Retiree } from "../ts/retiree";
import { SecureSection } from "../ts/secure/secureSection";
import questions from "../ts/secure/questions";
import { getPensionPotDrawdownAmounts } from "../ts/utilities";
import { app } from "../ts/app";
import ConsentType from "../ts/consent/consentType";
import ExternalAgency from "../ts/consent/externalAgency";
import { hasConsented } from "../ts/utilities";
import Result from "../ts/result";
import PermittedEntityType from "../ts/consent/permittedEntityType";
import ScenarioOutcome from "../ts/scenarioOutcome";
import Temporal from "@/shared/ts/temporal";
import Constants from "../ts/constants";
import { ApplicationInfo } from "../ts/secure/applicationInfo";

enum DisplayMode {
    PreLaunch,
    PostLaunch
}

const allowDisplayModeChange = true;

export default /*@__PURE__*/_defineComponent({
  __name: 'Secure',
  setup(__props) {

const store = useStore();
const investmentAllocation = ref(null);
const apply = ref(null);
const retiree = computed<Retiree | null>(() => store.state.retiree);
const displayMode = ref(DisplayMode.PreLaunch);
const registerInterest = ref(false);
const toggleConsentErrorMessage = ref("");
let suppressConsentAction = false;

watch(
    () => registerInterest.value,
    async (newValue: boolean) => {
        if (suppressConsentAction) {
            suppressConsentAction = false;
            return;
        }

        toggleConsentErrorMessage.value = "";
        let toggleConsentResult: Result;

        if (registerInterest.value) {
            toggleConsentResult = await app.grantConsent(PermittedEntityType.ExternalAgency, ExternalAgency.Guiide, ConsentType.SendProductNotifications, true);
        } else {
            toggleConsentResult = await app.revokeConsent();
        }

        if (!toggleConsentResult.successful) {
            const consented = hasConsented(ExternalAgency.Guiide, ConsentType.SendProductNotifications);
            if (registerInterest.value != consented) {
                suppressConsentAction = true;
                registerInterest.value = !newValue;
            }
            toggleConsentErrorMessage.value = toggleConsentResult.message;
        }
    }
)

const toggleConsentDisabled = computed<boolean>(() => store.state.transactionOngoing);

watch(
    () => store.state.retiree,
    (newRetiree: Retiree, oldRetiree: Retiree) => {
        const consented = hasConsented(ExternalAgency.Guiide, ConsentType.SendProductNotifications);

        const isInitialLoadAndConsentStatusIsGoingToBeChanged = oldRetiree == null && registerInterest.value != consented;
        if (isInitialLoadAndConsentStatusIsGoingToBeChanged) {
            suppressConsentAction = true;
        }

        registerInterest.value = consented;
    }
)

const questionAndAnswerQuestions = () => questions.filter(q => q.section == SecureSection.QuestionsAndAnswers);

const pensionPotDrawdownAmounts = computed<Array<number>>(() => {
    return getPensionPotDrawdownAmounts(store.state.plan);
})

const productLive = computed<boolean>(() => { return store.state.configuration.secureProductApplyActive; });

const whatStepsTitle = computed<string>(() => {
    return productLive.value
        ? "What steps are needed"
        : "What steps are needed when this is available?";
});

const rememberToRegisterPrompt = computed<string>(() => {
    if (store.state.retiree == null) {
        return "";
    }

    return store.state.retiree.isRegistered
        ? ""
        : "Don't forget to save your plan on the previous page.";
})

const nextButtonInfo = computed<{ display: boolean, link: string }>(() => {
    return {
        display: store.state.configuration.finishDisplaysNextButton && displayMode.value == DisplayMode.PostLaunch,
        link: store.state.configuration.nav.track.resource
    };
});

function toggleDisplayMode() {
    if (allowDisplayModeChange) {
        displayMode.value = displayMode.value == DisplayMode.PreLaunch ? DisplayMode.PostLaunch : DisplayMode.PreLaunch;
    }
}

async function onApply(selectedPensionPots: { pensionPotIds: Array<number> }) {
    const allocationInfo = investmentAllocation.value.getAllocationInformation();

    const applicationInfo: ApplicationInfo = {
        pensionPotIds: selectedPensionPots.pensionPotIds,
        matchedYears: allocationInfo.matchedYears,
        taxFreeLumpSumFundId: allocationInfo.taxFreeLumpSumFundId,
        fundAllocCalcAtPresent: allocationInfo.fundAllocCalcAtPresent,
        fundAllocCalcAtRetirement: allocationInfo.fundAllocCalcAtRetirement
    };

    const result = await app.beginPensionTransfer(applicationInfo);

    if (!result.successful) {
        apply.value.setErrorMessage(result.message);
    }
}

const isEligibleForProduct = computed<boolean>(() => {
    if (retiree.value == null) {
        return true;
    }

    if (retiree.value.alreadyDrawingPension) {
        return false;
    }

    if (retiree.value.pensionPots.pots.length == 0) {
        return false;
    }

    const accordingToPlanScenario = store.state.accordingToPlanScenario;

    if (accordingToPlanScenario == null) {
        return true;
    }

    if (accordingToPlanScenario.outcome == ScenarioOutcome.Fail) {
        return false;
    }

    const currentAge = Temporal.calcAge(new Date(retiree.value.dateOfBirth));

    if (currentAge < Constants.Product.MinEligibleAge) {
        return false;
    }

    return true;
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createVNode(WorkflowNavigation, { workflowStage: "Secure" }),
      _createElementVNode("h2", {
        class: "skin-page-heading",
        onDblclick: _cache[0] || (_cache[0] = ($event: any) => (toggleDisplayMode()))
      }, " Make your plan a reality ", 32)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _cache[24] || (_cache[24] = _createElementVNode("p", { class: "mx-5 mb-4" }, [
        _createTextVNode(" You have built a sustainable plan with Guiide to get what you want, reduce tax and not run out of money. If you don't pay for advice, you will now need to work out a few things for yourself. These are how to invest your pots and taking the amount you want each month, which is far from easy. On top of this you will need to keep track of your plan to get the best chance of making it work. "),
        _createElementVNode("span", { class: "fw-bold" }, "Or there is a new option…")
      ], -1)),
      _createVNode(SubSection, {
        title: "The new option",
        showHelp: false
      }, {
        default: _withCtx(() => [
          _cache[3] || (_cache[3] = _createElementVNode("p", null, [
            _createTextVNode(" Guiide are working with a pension provider and investment manager to provide a "),
            _createElementVNode("span", { class: "fw-bold" }, "new unique pension product to do all this for you"),
            _createTextVNode(". It will provide another helping hand at the wheel to make things much simpler for you to get a great retirement outcome. ")
          ], -1)),
          _cache[4] || (_cache[4] = _createElementVNode("p", null, "Here is a summary of what you can expect when we launch this product with our partners later this year.", -1)),
          _cache[5] || (_cache[5] = _createElementVNode("p", { class: "fw-bold" }, "Planning - Guiide will take care of all of this for you", -1)),
          _cache[6] || (_cache[6] = _createElementVNode("p", null, "Our tools will help you build, live track, review and adjust your plan over time.", -1)),
          _cache[7] || (_cache[7] = _createElementVNode("p", null, " By the time we launch we will be allowing for partner's income also to improve our plan building further. This would allow you to bring both plans into the product if wanted so all your household pension pot income is coming from one place. ", -1)),
          _cache[8] || (_cache[8] = _createElementVNode("p", { class: "fw-bold" }, "Payments - The pension provider will do all this automatically for you", -1)),
          _cache[9] || (_cache[9] = _createElementVNode("p", null, " The FCA regulated pension provider will consolidate the pensions you wish to move into the product, take any contribution payments you want to make and most importantly when the time comes, pay any tax free cash lump sum wanted and the income payments you want in your plan each month. ", -1)),
          _cache[10] || (_cache[10] = _createElementVNode("p", null, "Don't worry if you want to change your plan or take ad hoc withdrawals, these can be facilitated.", -1)),
          _cache[11] || (_cache[11] = _createElementVNode("p", { class: "fw-bold" }, "Investments - The investment manager will provide the fund splits for your plan and needs", -1)),
          _cache[12] || (_cache[12] = _createElementVNode("p", null, " The FCA regulated investment manager will split your funds between three of their investment funds. This split is calculated for you based on your expected annual plan payments, when you want to retire (if you haven't already), whether you want a tax free cash lump sum, plus the number of years of future years payments you want to match more closely (more on this later). These are: ", -1)),
          _cache[13] || (_cache[13] = _createElementVNode("ul", null, [
            _createElementVNode("li", null, [
              _createElementVNode("span", { class: "fst-italic" }, "Money Fund"),
              _createTextVNode(" - to cover any tax free cash lump sums payable at retirement plus 1% to cover charges")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("span", { class: "fst-italic" }, "Matching Income Fund"),
              _createTextVNode(" - to cover the income needs you want to match")
            ]),
            _createElementVNode("li", null, [
              _createElementVNode("span", { class: "fst-italic" }, "Responsible Growth Fund"),
              _createTextVNode(" - to try to achieve additional growth over the longer term")
            ])
          ], -1)),
          (!isEligibleForProduct.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, _cache[2] || (_cache[2] = [
                _createTextVNode(" Unfortunately we are unable to offer this to you at this time. There are a number of reasons why this might be the case: "),
                _createElementVNode("ul", { class: "mt-2" }, [
                  _createElementVNode("li", null, "You are already taking cash or income from your pot. The product can't accept transfers from these pots right now, but will do in later versions."),
                  _createElementVNode("li", null, "You are under 50 years of age, later versions may accept under 50s."),
                  _createElementVNode("li", null, "You have no pension pots, just savings pots or income. A similar non pension (i.e. savings) product may be developed later."),
                  _createElementVNode("li", null, "You have shortfalls in your plan even if everything goes to plan. The product is designed to put an initial lifetime sustainable plan into practice. You need to use one of the Solves in Design or Test to remove any shortfalls.")
                ], -1)
              ])))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      (isEligibleForProduct.value)
        ? (_openBlock(), _createBlock(SubSection, {
            key: 0,
            title: "How do we see the product working?",
            showHelp: false
          }, {
            default: _withCtx(() => _cache[14] || (_cache[14] = [
              _createElementVNode("p", null, " Firstly, based on your Guiide plan, choose how many of your future years payments you want to match more closely. ", -1),
              _createElementVNode("p", { class: "fw-bold" }, "What do we mean by matched more closely?", -1),
              _createElementVNode("p", null, [
                _createTextVNode(" The investment manager will split your money between three of their investment funds. Money Fund, Responsible Growth Fund and a Matching Income Fund, to match your annual payments more closely. The Matching Income Fund gets most of its return from payments from the underlying investments, rather than capital growth. Regardless of market ups and downs you can expect similar levels of income. This means there is a very low chance of a negative total return (income plus growth) over three years. "),
                _createElementVNode("span", { class: "fw-bold" }, "This is really important when taking money each year from your pots.")
              ], -1),
              _createElementVNode("p", null, " The pension provider will then take your Guiide plan and organise the investments based on your plan payments and the number of matched years of income that you choose to have. ", -1)
            ])),
            _: 1
          }))
        : _createCommentVNode("", true),
      (isEligibleForProduct.value)
        ? (_openBlock(), _createBlock(InvestmentAllocation, {
            key: 1,
            ref_key: "investmentAllocation",
            ref: investmentAllocation,
            retiree: retiree.value,
            pensionPotDrawdownAmounts: pensionPotDrawdownAmounts.value
          }, null, 8, ["retiree", "pensionPotDrawdownAmounts"]))
        : _createCommentVNode("", true),
      (isEligibleForProduct.value)
        ? (_openBlock(), _createBlock(SubSection, {
            key: 2,
            title: whatStepsTitle.value,
            showHelp: false
          }, {
            default: _withCtx(() => [
              _cache[17] || (_cache[17] = _createElementVNode("p", null, "First you will need to open a pension with the provider.", -1)),
              _cache[18] || (_cache[18] = _createElementVNode("p", null, " Once done they will organise the transfer in of any pots you have, sort out the initial investment split, and arrange for any added contributions to be collected if you plan to make any. ", -1)),
              _cache[19] || (_cache[19] = _createElementVNode("p", null, " Finally when you want to start taking your income, Guiide will agree your initial plan with you. The pension provider will pay exactly what you want each month until the end of that tax year. ", -1)),
              _cache[20] || (_cache[20] = _createElementVNode("p", null, " Your pension value, investments and other information will be shown in Guiide and will feed directly into your plan so you can see at any time if it remains on track. You can also view your pension information online with the pension provider. ", -1)),
              _cache[21] || (_cache[21] = _createElementVNode("p", null, "At the end of each tax year in March you can review and change your long term plan and length of matched income wanted.", -1)),
              _cache[22] || (_cache[22] = _createElementVNode("p", null, "The payments you will receive and the investment split then change as necessary from the next tax year in April. It's that simple!", -1)),
              (!productLive.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[16] || (_cache[16] = _createElementVNode("p", { class: "fw-bold" }, null, -1)),
                    _createElementVNode("div", _hoisted_5, [
                      _cache[15] || (_cache[15] = _createElementVNode("div", { class: "card-header" }, [
                        _createElementVNode("strong", null, "How do I register interest?")
                      ], -1)),
                      _createElementVNode("div", _hoisted_6, [
                        _createElementVNode("div", _hoisted_7, [
                          _withDirectives(_createElementVNode("input", {
                            id: "consentToProductNotificationsCheckboxId",
                            type: "checkbox",
                            class: "form-check-input todays-money-chk",
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((registerInterest).value = $event)),
                            role: "switch",
                            disabled: toggleConsentDisabled.value
                          }, null, 8, _hoisted_8), [
                            [_vModelCheckbox, registerInterest.value]
                          ]),
                          _createElementVNode("label", _hoisted_9, " Tick this box if you may be interested when available. " + _toDisplayString(rememberToRegisterPrompt.value), 1)
                        ])
                      ])
                    ]),
                    _createVNode(DynamicAdviceViewer, { message: toggleConsentErrorMessage.value }, null, 8, ["message"])
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["title"]))
        : _createCommentVNode("", true),
      (isEligibleForProduct.value)
        ? (_openBlock(), _createBlock(SubSection, {
            key: 3,
            title: "Questions you may have",
            showHelp: false
          }, {
            default: _withCtx(() => [
              _cache[23] || (_cache[23] = _createElementVNode("p", null, " We have detailed commonly asked questions below but if you would like to know anything not covered here please email us at contact@guiide.co.uk. ", -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(questionAndAnswerQuestions(), (question, i) => {
                return (_openBlock(), _createBlock(QuestionAndAnswer, {
                  key: i,
                  "show-answer": question.showAnswerByDefault,
                  "show-caret": !question.showAnswerByDefault,
                  "show-border": false
                }, {
                  question: _withCtx(() => [
                    _createTextVNode(_toDisplayString(question.question), 1)
                  ]),
                  default: _withCtx(() => [
                    _createElementVNode("p", {
                      innerHTML: question.answer
                    }, null, 8, _hoisted_10)
                  ]),
                  _: 2
                }, 1032, ["show-answer", "show-caret"]))
              }), 128))
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (isEligibleForProduct.value && productLive.value)
        ? (_openBlock(), _createBlock(Apply, {
            key: 4,
            ref_key: "apply",
            ref: apply,
            retiree: retiree.value,
            onApply: onApply
          }, null, 8, ["retiree"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_11, [
        _createVNode(PageNav, {
          "show-next": nextButtonInfo.value.display,
          "back-link": "finish",
          "next-link": nextButtonInfo.value.link,
          "next-text": "Next"
        }, null, 8, ["show-next", "next-link"])
      ])
    ])
  ]))
}
}

})